import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { cn } from "lib/utils";
import { useTheme } from "contexts/ThemeContext";
import Sidebar from "./ui/sidebar/Sidebar";
import AppHeader from "./ui/header/AppHeader";
import { SidebarProvider } from "./SidebarContext";
import CommandPalette from "components/CommandPalette";
import merkoLogo from "../../assets/merko-logo.svg";
import merkoLightLogo from "../../assets/Merko_dark_Logo.svg";

/* ══════════════════════════════════════════════════════════════════════════
   AppShell

   The chrome that wraps every route: sidebar, header, scroll region.

   This was ~90 lines of JSX at the bottom of a 2548-line App.tsx, tangled
   with that component's 20+ handlers and 10+ state variables. Extracting it
   verbatim — same 100dvh sizing, same overscroll containment, same
   `data-scroll-container` hook, same touch-safe bottom spacer, same
   full-bleed route handling — means the shell can be reasoned about and
   reused without touching any page logic.

   Behaviour intentionally preserved:
   • `100dvh` + `-webkit-fill-available` so iOS browser chrome is excluded.
   • `overscroll-behavior: none` on the frame and `contain` on the scroller,
     which is what stops the iOS rubber-band from dragging the header past
     the safe area.
   • Routes that own the whole viewport (Drawflow canvas, template editor, AI
     playground) get a fixed-height, non-scrolling content area.
   • The Drawflow canvas hides the sidebar and shows the brand mark inline.
   ══════════════════════════════════════════════════════════════════════════ */

/** Routes that manage their own scrolling and need an exact-height container. */
const FULL_HEIGHT_ROUTES = ["/campaigns/flow", "/ai-playground"];
const FULL_HEIGHT_PREFIXES = ["/templates/editor/"];
/** Routes that take over the entire viewport, sidebar included. */
const IMMERSIVE_ROUTES = ["/campaigns/flow"];

function useIsTouchDevice(): boolean {
    const [isTouch, setIsTouch] = useState(false);
    useEffect(() => {
        setIsTouch(window.matchMedia("(pointer: coarse)").matches);
    }, []);
    return isTouch;
}

export interface AppShellProps {
    children: React.ReactNode;
    /** Extra controls injected into the header for the current route */
    headerActions?: React.ReactNode;
}

const AppShellInner: React.FC<AppShellProps> = ({ children, headerActions }) => {
    const location = useLocation();
    const { isDark } = useTheme();
    const mainRef = useRef<HTMLElement>(null);
    const isTouchDevice = useIsTouchDevice();
    const [paletteOpen, setPaletteOpen] = useState(false);

    const isImmersive = useMemo(
        () => IMMERSIVE_ROUTES.includes(location.pathname),
        [location.pathname]
    );

    const useFixedHeight = useMemo(
        () =>
            FULL_HEIGHT_ROUTES.includes(location.pathname) ||
            FULL_HEIGHT_PREFIXES.some((prefix) => location.pathname.startsWith(prefix)),
        [location.pathname]
    );

    /* ── ⌘K / Ctrl-K opens search from anywhere.
          The palette existed but was only mounted inside a layout that was
          never rendered, so the shortcut did nothing in the shipped app. ── */
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setPaletteOpen((open) => !open);
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    /* Reset scroll position on navigation. */
    useEffect(() => {
        mainRef.current?.scrollTo?.({ top: 0 });
    }, [location.pathname]);

    /* ── iOS scroll containment (moved verbatim from App.tsx) ──
       Two layers, both required:
       1. Swallow touchmove at the scroller's own boundaries so a drag past the
          top or bottom cannot bubble into a page-level rubber-band.
       2. A document-level guard that cancels touchmove unless the gesture
          started inside something that genuinely scrolls — walking up from the
          target and honouring `data-scroll-container`, `data-sheet-scroll` and
          any element whose overflow classes come with real overflow content. */
    useEffect(() => {
        const el = mainRef.current;
        if (!el) return;

        let startY = 0;

        const onTouchStart = (e: TouchEvent) => {
            startY = e.touches[0].clientY;
        };

        const onTouchMove = (e: TouchEvent) => {
            const { scrollTop, scrollHeight, clientHeight } = el;
            const deltaY = e.touches[0].clientY - startY;

            if (deltaY > 0 && scrollTop <= 0) {
                e.preventDefault();
                return;
            }
            if (deltaY < 0 && scrollTop + clientHeight >= scrollHeight - 1) {
                e.preventDefault();
                return;
            }
            if (scrollHeight <= clientHeight) {
                e.preventDefault();
            }
        };

        el.addEventListener("touchstart", onTouchStart, { passive: true });
        el.addEventListener("touchmove", onTouchMove, { passive: false });

        return () => {
            el.removeEventListener("touchstart", onTouchStart);
            el.removeEventListener("touchmove", onTouchMove);
        };
    }, []);

    useEffect(() => {
        const SCROLL_CLASS_RE = /\boverflow-(?:y-)?(?:auto|scroll)\b/;

        const blockElasticScroll = (e: TouchEvent) => {
            if (e.touches.length > 1) return;

            let node = e.target as HTMLElement | null;
            while (node && node !== document.documentElement) {
                if (
                    node.dataset?.scrollContainer !== undefined ||
                    node.dataset?.sheetScroll !== undefined
                ) return;
                if (node.className && typeof node.className === "string") {
                    if (SCROLL_CLASS_RE.test(node.className)) {
                        if (node.scrollHeight > node.clientHeight) return;
                    }
                }
                node = node.parentElement;
            }
            e.preventDefault();
        };

        document.addEventListener("touchmove", blockElasticScroll, { passive: false });
        return () => document.removeEventListener("touchmove", blockElasticScroll);
    }, []);

    return (
        <>
            {/* Bypass the nav — the first thing a keyboard user reaches. */}
            <a href="#main-content" className="skip-link">
                Skip to main content
            </a>

            <div
                className="flex bg-canvas"
                style={{
                    height: "100dvh",
                    minHeight: "-webkit-fill-available",
                    overflow: "hidden",
                    overscrollBehavior: "none",
                }}
            >
                {!isImmersive && <Sidebar />}

                <div className="flex flex-col flex-1 relative z-10 min-w-0 overflow-hidden">
                    <AppHeader
                        onOpenSearch={() => setPaletteOpen(true)}
                        actions={headerActions}
                        titleSlot={
                            isImmersive ? (
                                <img
                                    src={isDark ? merkoLogo : merkoLightLogo}
                                    className="h-9"
                                    alt="Merko"
                                />
                            ) : undefined
                        }
                    />

                    <main
                        id="main-content"
                        ref={mainRef}
                        data-scroll-container
                        role="main"
                        tabIndex={-1}
                        className={cn(
                            "flex-1 bg-canvas focus:outline-none",
                            useFixedHeight
                                ? "h-full overflow-hidden"
                                : "overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 dark:scrollbar-thumb-dark-600 scrollbar-track-gray-100 dark:scrollbar-track-dark-800"
                        )}
                        style={{ overscrollBehavior: "contain" }}
                    >
                        <div className={useFixedHeight ? "h-full" : "min-h-full flex flex-col"}>
                            <div className={useFixedHeight ? "h-full" : "flex-1"}>{children}</div>

                            {/* Breathing room above the on-screen browser chrome */}
                            {isTouchDevice && !useFixedHeight && (
                                <div aria-hidden="true" className="touch-safe-bottom flex-shrink-0" />
                            )}
                        </div>
                    </main>
                </div>
            </div>

            <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
        </>
    );
};

export const AppShell: React.FC<AppShellProps> = (props) => (
    <SidebarProvider>
        <AppShellInner {...props} />
    </SidebarProvider>
);

export default AppShell;
